import axios, { type AxiosInstance } from "axios"
import { toast } from "sonner"
import { useAuthStore } from "@/store/useAuthStore"
import { getApiBaseUrl } from "@/lib/api-base-url"
import { markErrorToasted } from "@/lib/api-toast"
import { readTokenCookie, resolveUnauthorized } from "@/lib/auth-401"
import { messageFromErrorBody } from "@/lib/error-message"
import { planErrorToast } from "@/lib/error-toast-gate"
import { namesFields } from "@/lib/field-errors"

/** When each error toast last showed — the gate's memory (lib/error-toast-gate), for this tab. */
const toastSeen = new Map<string, number>()

/**
 * `suppressToast`, DECLARED — the response interceptor below has always read
 * it, but nothing ever told TypeScript it exists. Every caller that wanted a
 * quiet request had to cast its config to `any` (see `features/ai-nft/api.ts`),
 * which switches off checking for the whole object — including the fields
 * axios does define.
 *
 * Augmenting the module types it once means a quiet request is now ordinary,
 * checked code, and removes the reason to reach for `any` here at all.
 */
declare module "axios" {
  export interface AxiosRequestConfig {
    /**
     * Skip the interceptor's automatic error toast for this request.
     *
     * For calls whose failure the caller reports itself, or reports nowhere: a
     * poll that retries, an upload that shows its own inline error. A 401 still
     * prompts a login regardless (and ends the session when it was the live
     * one, see lib/auth-401.ts) — that is not a toast, it is a session ending.
     */
    suppressToast?: boolean
    /**
     * This request comes from a form that shows a 422's field messages under
     * its own fields (lib/api-toast `reportFormError`). A 422 that names
     * fields is then left to the form — no toast here; anything else is
     * toasted as usual.
     */
    fieldErrors?: boolean
  }
}

const remoteBaseURL = process.env.NEXT_PUBLIC_API_BASE_URL || "/api"

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: remoteBaseURL,
  timeout: 15000,
  // withCredentials: true,
})

axiosInstance.defaults.headers.common["Accept"] = "application/json"

// The auth store persists to localStorage but uses `skipHydration: true` (it's
// manually rehydrated from a useEffect in ClientLayout, to avoid an SSR/CSR
// hydration-mismatch flash on the very first paint). That rehydration is
// async relative to component mount, so any request fired before it finishes
// (e.g. a list/grid that fetches on mount) would read `token: null` here and
// go out unauthenticated — silently losing the current user's `current_user`
// like/watchlist/follow flags for that request, even though the user is
// logged in. Waiting for hydration to finish before the *first* request
// (subsequent calls see `hasHydrated() === true` and skip this immediately)
// closes that race without touching component render timing at all.
let authHydration: Promise<void> | null = null
function waitForAuthHydration(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve()
  if (useAuthStore.persist.hasHydrated()) return Promise.resolve()
  if (!authHydration) {
    authHydration = new Promise<void>((resolve) => {
      const unsub = useAuthStore.persist.onFinishHydration(() => {
        unsub()
        resolve()
      })
      useAuthStore.persist.rehydrate()
    })
  }
  return authHydration
}

axiosInstance.interceptors.request.use(async (config) => {
  await waitForAuthHydration()
  if (!(config as any)._customBase) {
    config.baseURL = getApiBaseUrl() || remoteBaseURL
  }
  const headers = config.headers ?? {}
  const token = useAuthStore.getState().token

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }
  // Remembered so a 401 can be judged against the session it was sent with,
  // not whatever the store holds when the response lands (lib/auth-401.ts).
  ;(config as any)._authToken = token ?? null

  if (typeof window !== "undefined") {
    const seg = window.location.pathname.split("/")[1]
    if (seg === "fa" || seg === "en") {
      ;(headers as any)["lang"] = seg
    }
  }
  config.headers = headers

  // File uploads (the only multipart requests we send) get no timeout. The
  // 15s instance default is sized for JSON round-trips and was silently
  // acting as a *file-size* limit: anything that takes longer than 15s to
  // push — a large image on a slow uplink — aborted client-side as a network
  // error before the backend ever saw it, so the backend's own size rules
  // could never be the thing that decides. Enforcing the limit is the
  // backend's job; we just have to let the request finish.
  // Keyed on the body being a FormData rather than on the Content-Type
  // header: by this point `headers` is an AxiosHeaders instance that has
  // normalised the key casing, so a literal `headers["Content-Type"]` lookup
  // silently misses.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    config.timeout = 0
  }

  return config
})

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (typeof window !== "undefined") {
      const seg = window.location.pathname.split("/")[1];
      const isFa = seg === "fa";
      const config = error.config as any;

      if (error.response?.status === 401) {
        const verdict = resolveUnauthorized(config?._authToken ?? null, readTokenCookie(document.cookie))
        if (verdict === "adopt-newer") {
          // This tab is behind: another tab signed in after it loaded. Pick
          // that session up and retry once, instead of logging everyone out.
          if (!config?._retriedWithNewerToken) {
            return Promise.resolve(useAuthStore.persist.rehydrate()).then(() =>
              axiosInstance({ ...config, _retriedWithNewerToken: true })
            )
          }
        } else if (verdict === "end-session") {
          useAuthStore.getState().logout() // Clear session
        }
        // One sign-in toast, however many requests the expired session failed at once.
        toast.error(isFa ? "لطفا ابتدا وارد حساب کاربری خود شوید" : "Please login to continue", { id: "api-error:auth" });
        // Marked like any other toasted error: a catch that adds "failed to
        // save" on top of "please log in" is the same stacking problem, and
        // the login prompt is the more useful of the two.
        markErrorToasted(error)
        useAuthStore.getState().openLoginModal()
      } else if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
        // A request cancelled on purpose (a page left, a search typed over) failed at nothing: no toast.
      } else if (config?.fieldErrors && error.response?.status === 422 && namesFields(error.response?.data)) {
        // The form puts these under its fields (lib/field-errors) — and toasts whatever it cannot place.
      } else if (!config?.suppressToast) {
        // Only show toast if suppressToast is NOT true.
        // The API often puts the real reason (e.g. insufficient balance) in
        // `errors` with a null `message` — check both before the generic text.
        // Some refusals carry it one level down (`data.message`) — see lib/error-message.
        const message = messageFromErrorBody(error.response?.data) || (isFa ? "عملیات با خطا مواجه شد" : "Operation failed");
        // Gated (lib/error-toast-gate): one toast per message, a failed read once per quiet window, the
        // server unreachable as one plain line — never the same toast several times a second.
        const plan = planErrorToast({
          method: config?.method,
          hasResponse: Boolean(error.response),
          message,
          networkMessage: isFa ? "ارتباط با سرور برقرار نشد؛ کمی بعد دوباره امتحان کنید." : "Can't reach the server — try again shortly.",
        }, toastSeen, Date.now());
        if (plan.show) toast.error(plan.message, { id: plan.id });
        // THIS TOAST IS THE ONE THE USER SHOULD SEE. Everything downstream
        // asks `wasErrorToasted` before adding its own, so a feature's
        // generic "…failed" line can no longer stack on top of, and hide,
        // the reason the server actually gave. See `lib/api-toast.ts`.
        markErrorToasted(error);
      }
    }
    return Promise.reject(error)
  }
)
